import { AppleIcon } from "@/components/icons/Icons";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@/config/constants";
import { PATHS } from "@/config/paths";
import { useMobile } from "@/providers/mobileProvider";
import { useTranslation } from "@/hooks/useTranslation";
import { useRouter } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface DownloadButtonsProps {
  isHero?: boolean;
  hideStartButton?: boolean;
  className?: string;
}

export function DownloadButtons({
  isHero = false,
  hideStartButton = false,
  className,
}: DownloadButtonsProps) {
  const router = useRouter();
  const { isPWA } = useMobile();
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={cn(
        "flex flex-col md:flex-row justify-center lg:justify-start gap-3 w-full max-w-md lg:max-w-none relative z-10",
        className,
      )}
    >
      {!hideStartButton && (
        <button
          data-testid={isHero ? "hero-start-button" : undefined}
          type="button"
          onClick={() => router.navigate({ to: PATHS.DASHBOARD })}
          className="group relative flex-1 lg:flex-none lg:w-fit px-6 py-3.5 rounded-full bg-primary text-white transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] backdrop-blur-sm dark:border dark:border-gray-700 dark:bg-primary dark:text-white dark:hover:bg-primary/90 cursor-pointer"
        >
          <span className="flex items-center justify-center gap-2 text-lg font-medium whitespace-nowrap">
            {t("cta.start_now")}
          </span>
        </button>
      )}
      {!isPWA && (
        <>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("footer.downloadAppStore")}
            className="flex group relative flex-1 lg:flex-none lg:w-fit p-4 rounded-full dark:border bg-white text-[#211E1F] hover:bg-[#211E1F]/5 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] backdrop-blur-sm dark:border-gray-700 dark:bg-black dark:text-white dark:hover:bg-white/10"
          >
            <span className="flex items-center justify-center gap-2 text-base font-medium whitespace-nowrap w-full">
              <AppleIcon className="size-5" />
              App Store
            </span>
          </a>
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("footer.downloadGooglePlay")}
            className="flex group relative flex-1 lg:flex-none lg:w-fit p-4 rounded-full dark:border bg-white text-[#211E1F] hover:bg-[#211E1F]/5 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] backdrop-blur-sm dark:border-gray-700 dark:bg-black dark:text-white dark:hover:bg-white/10"
          >
            <span className="flex items-center justify-center gap-2 text-base font-medium whitespace-nowrap w-full">
              <svg className="size-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <title>Google Play</title>
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
              Google Play
            </span>
          </a>
        </>
      )}
    </motion.div>
  );
}
